import { defineStore } from "pinia";
import socket from "@/services/socket";
import { useDialogStore } from "./useDialogStore";
import { useLocaleStore } from "./useLocaleStore";
import { usePlayersStore } from "./usePlayersStore";
import { useSessionStore } from "./useSessionStore";
import type { ChatChannel, ChatMessage, Player } from "@/types";

interface ChatState {
  activeTabOld: ChatChannel | null;
  activeTab: ChatChannel;
  open: boolean;
  targetPlayer: string; // Player id
  messages: Record<ChatChannel, ChatMessage[]>;
}

export const useChatStore = defineStore("chat", {
  state: (): ChatState => ({
    activeTabOld: null,
    activeTab: "global",
    open: true,
    targetPlayer: "",
    messages: {
      left: [] as ChatMessage[],
      right: [] as ChatMessage[],
      global: [] as ChatMessage[],
      whispers: [] as ChatMessage[],
      host: [] as ChatMessage[],
      logs: [] as ChatMessage[],
      system: [] as ChatMessage[],
    },
  }),

  actions: {
    addMessage(tab: ChatChannel, message: ChatMessage) {
      message.timestamp = Date.now();
      this.messages[tab]?.push(message);
    },

    // Host-only entry in the logs tab (no sender, shown as a system line)
    addLog(text: string, senderId: string = "system", senderName: string = "") {
      if (useSessionStore().isPlayerOrSpectator || !text.trim()) return;
      this.addMessage("logs", {
        fromId: senderId,
        fromName: senderName,
        toId: "",
        toName: "",
        text,
        isRead: false,
        timestamp: Date.now(),
      });
    },

    // Localized log; "{name}" placeholders are replaced with vars
    logEvent(key: string, vars: Record<string, string | number> = {}) {
      let text = useLocaleStore().t(key);
      for (const [name, value] of Object.entries(vars)) {
        text = text.split(`{${name}}`).join(String(value));
      }
      this.addLog(text);
    },

    async clearMessages(autoConfirm: boolean = false) {
      const locale = useLocaleStore();
      const dialogStore = useDialogStore();
      const t = locale.t;
      this.$reset();
      const session = useSessionStore();
      if (!session.isPlayerOrSpectator) {
        if (autoConfirm || (await dialogStore.confirm(t("prompt.clearAllChat")))) {
          socket.send("clearChat");
        }
      }
    },

    markSignalAcknowledged(signalId: string) {
      const msg = this.messages["logs"]?.find((m) => m.signalId === signalId);
      if (msg) msg.acknowledged = true;
    },

    receiveMessage(msg: ChatMessage, tab: ChatChannel) {
      const playersStore = usePlayersStore();
      const localeStore = useLocaleStore();

      msg.isRead = false;

      const fromPlayer = playersStore.getById(msg.fromId);
      msg.fromName =
        msg.fromId === "host" || msg.fromId === ""
          ? localeStore.t("chat.host")
          : fromPlayer?.name || msg.fromId;

      const toPlayer = playersStore.getById(msg.toId);
      msg.toName =
        msg.toId === "host" || msg.toId === ""
          ? localeStore.t("chat.host")
          : toPlayer?.name || msg.toId || "Undefined";

      this.addMessage(tab, msg);
    },

    pick(player: Player) {
      this.targetPlayer = player.id;
      this.activeTab = "host";
      this.open = true;
    }
  },
  persist: true,
});
